import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Save, Filter } from "lucide-react";
import { StaffLedgers } from "./StaffLedger";
import { fmtINR, fmtDate } from "@/utils/format";
import { toast } from "sonner";
import { Mini } from "./AdminDailyReports";

const isoDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

export function DiffBadge({ v, testId }) {
  if (v === null || v === undefined) return <span className="text-slate-400 text-xs" data-testid={testId}>pending</span>;
  const cls = v < 0 ? "text-red-600" : v > 0 ? "text-emerald-700" : "text-slate-600";
  const label = v < 0 ? "Shortage" : v > 0 ? "Excess" : "Balanced";
  return <span className={`font-mono-num font-semibold ${cls}`} data-testid={testId}>{v > 0 ? "+" : ""}{fmtINR(v)} <span className="text-[10px] font-sans font-normal">{label}</span></span>;
}

export default function AdminCash() {
  const [tab, setTab] = useState("shifts");
  return (
    <AdminLayout>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-display font-bold">Staff Cash Reconciliation</h1>
          <p className="text-sm text-slate-500 mt-1">Enter actual cash received per shift · each employee has a continuous ledger of shortage / excess, advances and returns.</p>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="cash-tabs">
            <TabsTrigger value="shifts" data-testid="tab-cash-shifts">Shift entries</TabsTrigger>
            <TabsTrigger value="ledgers" data-testid="tab-cash-ledgers">Staff ledgers</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {tab === "shifts" ? <ShiftEntries /> : <StaffLedgers />}
    </AdminLayout>
  );
}

function ShiftEntries() {
  const [range, setRange] = useState({ from_date: isoDaysAgo(6), to_date: isoDaysAgo(0) });
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/cash/shifts", { params: range }); setData(data); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (row) => {
    const d = drafts[row.shift_id] || {};
    const body = { remarks: d.remarks ?? row.remarks ?? null };
    if (d.diff !== undefined && d.diff !== "") body.difference = Number(d.diff);
    else {
      const actual = d.actual ?? row.actual_cash;
      if (actual === undefined || actual === null || actual === "") return toast.error("Enter actual cash received, or the shortage / excess");
      body.actual_cash = Number(actual);
    }
    setSaving(row.shift_id);
    try {
      await api.post(`/shifts/${row.shift_id}/reconcile`, body);
      toast.success(`Saved · ${row.employee_name} · Shift ${row.shift_number}`);
      setDrafts((x) => { const n = { ...x }; delete n[row.shift_id]; return n; });
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(null); }
  };

  const s = data?.summary;
  return (
    <>
      <Card className="p-4 rounded-2xl border-slate-200 mb-5">
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div><Label className="text-xs">From</Label><Input data-testid="cash-from" type="date" className="h-11 rounded-xl mt-1" value={range.from_date} onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))} /></div>
          <div><Label className="text-xs">To</Label><Input data-testid="cash-to" type="date" className="h-11 rounded-xl mt-1" value={range.to_date} onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))} /></div>
          <Button onClick={load} data-testid="cash-apply-btn" className="h-11 rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]"><Filter className="w-4 h-4 mr-2" /> Apply</Button>
        </div>
      </Card>
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="cash-period-summary">
          <Mini label="Pending shifts" value={s.pending_shifts} />
          <Mini label="Reconciled" value={s.reconciled_shifts} />
          <Mini label="Total shortage" value={fmtINR(s.shortage)} />
          <Mini label="Total excess" value={fmtINR(s.excess)} green />
        </div>
      )}
      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cash-shifts-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left">
              <tr>
                <th className="py-3 px-3">Date</th><th className="py-3 px-3">Shift</th><th className="py-3 px-3">Employee</th>
                <th className="py-3 px-3 text-right">Sale</th><th className="py-3 px-3 text-right">Expected Cash</th>
                <th className="py-3 px-3">Actual Cash Received</th><th className="py-3 px-3">or Short (−) / Excess (+)</th><th className="py-3 px-3">Remarks</th>
                <th className="py-3 px-3 text-right">Shortage / Excess</th><th className="py-3 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {!data && <tr><td colSpan={10} className="py-8 text-center text-slate-500">Loading…</td></tr>}
              {data?.rows?.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-slate-500">No completed shifts in this range.</td></tr>}
              {data?.rows?.map((r) => {
                const d = drafts[r.shift_id] || {};
                return (
                  <tr key={r.shift_id} data-testid={`cash-row-${r.shift_id}`} className={`border-t border-slate-100 ${r.actual_cash === null ? "bg-amber-50/40" : ""}`}>
                    <td className="py-2 px-3 whitespace-nowrap">{fmtDate(r.business_date)}</td>
                    <td className="py-2 px-3">S{r.shift_number} · {r.mpd}</td>
                    <td className="py-2 px-3 whitespace-nowrap">{r.employee_name} <span className="text-slate-400">({r.employee_code})</span></td>
                    <td className="py-2 px-3 text-right font-mono-num">{fmtINR(r.grand_sales)}</td>
                    <td className="py-2 px-3 text-right font-mono-num font-semibold">{fmtINR(r.expected_cash)}</td>
                    <td className="py-2 px-3">
                      <Input data-testid={`actual-cash-${r.shift_id}`} type="number" step="0.01" min="0" inputMode="decimal" className="h-9 w-32 rounded-lg font-mono-num"
                        value={d.actual ?? (r.actual_cash ?? "")} placeholder="₹"
                        onChange={(e) => setDrafts((x) => ({ ...x, [r.shift_id]: { ...d, actual: e.target.value, diff: "" } }))} />
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => setDrafts((x) => ({ ...x, [r.shift_id]: { ...d, diff: d.diff ? String(-Math.abs(Number(d.diff))) : "-", actual: "" } }))} className={`h-9 w-8 rounded-lg border text-sm font-bold ${(d.diff || "").startsWith("-") ? "bg-red-600 text-white border-transparent" : "border-slate-200 text-red-600"}`} title="Shortage" data-testid={`diff-short-${r.shift_id}`}>−</button>
                        <button type="button" onClick={() => setDrafts((x) => ({ ...x, [r.shift_id]: { ...d, diff: d.diff ? String(Math.abs(Number(d.diff))) : "", actual: "" } }))} className={`h-9 w-8 rounded-lg border text-sm font-bold ${d.diff && !String(d.diff).startsWith("-") ? "bg-emerald-600 text-white border-transparent" : "border-slate-200 text-emerald-700"}`} title="Excess" data-testid={`diff-excess-${r.shift_id}`}>+</button>
                        <Input data-testid={`diff-input-${r.shift_id}`} type="number" step="1" inputMode="numeric" className="h-9 w-24 rounded-lg font-mono-num" value={d.diff ?? ""} placeholder="e.g. -200"
                          onChange={(e) => setDrafts((x) => ({ ...x, [r.shift_id]: { ...d, diff: e.target.value, actual: "" } }))} />
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <Input data-testid={`remarks-${r.shift_id}`} className="h-9 w-40 rounded-lg" value={d.remarks ?? (r.remarks ?? "")} placeholder="optional"
                        onChange={(e) => setDrafts((x) => ({ ...x, [r.shift_id]: { ...d, remarks: e.target.value } }))} />
                    </td>
                    <td className="py-2 px-3 text-right"><DiffBadge v={r.difference} testId={`diff-${r.shift_id}`} /></td>
                    <td className="py-2 px-3 text-right">
                      <Button size="sm" disabled={saving === r.shift_id} onClick={() => save(r)} data-testid={`save-cash-${r.shift_id}`} className="rounded-full h-8 bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]">
                        <Save className="w-3.5 h-3.5 mr-1" /> {r.actual_cash === null ? "Save" : "Update"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

export function NetBadge({ v, testId }) {
  const n = Number(v || 0);
  if (n === 0) return <span className="font-mono-num text-slate-600" data-testid={testId}>₹0.00 · settled</span>;
  return (
    <span className={`font-mono-num font-bold ${n < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid={testId}>
      {fmtINR(Math.abs(n))} <span className="text-[10px] font-sans font-normal">{n < 0 ? "to pay" : "to receive"}</span>
    </span>
  );
}
